import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import type { OpponentUser } from "../lib/types";

export function NewGame() {
  const navigate = useNavigate();
  const [opponents, setOpponents] = useState<OpponentUser[]>([]);
  const [opponentId, setOpponentId] = useState<number | null>(null);
  const [color, setColor] = useState<"white" | "black" | "random">("random");
  const [mode, setMode] = useState<"async" | "live">("async");
  const [minutes, setMinutes] = useState(10);
  const [increment, setIncrement] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listOpponents()
      .then((list) => {
        setOpponents(list);
        if (list.length > 0 && list[0]) setOpponentId(list[0].id);
      })
      .catch((e) => setError((e as Error).message));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (opponentId === null) return;
    setBusy(true);
    setError(null);
    try {
      const body: Parameters<typeof api.createGame>[0] = {
        opponentUserId: opponentId,
        creatorColor: color,
        mode,
      };
      if (mode === "live") {
        body.initialClockMs = minutes * 60 * 1000;
        body.incrementMs = increment * 1000;
      }
      const { game } = await api.createGame(body);
      navigate(`/game/${game.id}`);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  if (opponents.length === 0) {
    return (
      <div className="page">
        <h1>New game</h1>
        <div className="card empty-state">
          <p className="big">There's nobody else in your family circle yet.</p>
          <p className="muted">Ask the admin to invite more family members.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-pad-bottom">
      <h1>New game</h1>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="opp">Play against</label>
          <select
            id="opp"
            value={opponentId ?? ""}
            onChange={(e) => setOpponentId(Number(e.target.value))}
          >
            {opponents.map((o) => (
              <option key={o.id} value={o.id}>{o.displayName}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>You play</label>
          <div className="flex-row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            {(["white", "black", "random"] as const).map((c) => (
              <button
                type="button"
                key={c}
                className={color === c ? "" : "secondary"}
                onClick={() => setColor(c)}
              >
                {c === "white" ? "♔ White" : c === "black" ? "♚ Black" : "🎲 Random"}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Game type</label>
          <div className="flex-row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            <button type="button" className={mode === "async" ? "" : "secondary"} onClick={() => setMode("async")}>
              Async (correspondence)
            </button>
            <button type="button" className={mode === "live" ? "" : "secondary"} onClick={() => setMode("live")}>
              Live (with clock)
            </button>
          </div>
          <p className="muted" style={{ fontSize: "0.95rem", marginTop: "0.5rem" }}>
            {mode === "async"
              ? "No clock. Play whenever you want — your opponent gets a Telegram ping after each move."
              : "Both players need to be online together. Each side has a clock."}
          </p>
        </div>

        {mode === "live" && (
          <>
            <div className="field">
              <label htmlFor="min">Minutes per side</label>
              <input
                id="min"
                type="number"
                min={1}
                max={180}
                value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label htmlFor="inc">Increment (seconds per move)</label>
              <input
                id="inc"
                type="number"
                min={0}
                max={60}
                value={increment}
                onChange={(e) => setIncrement(Number(e.target.value))}
              />
            </div>
          </>
        )}

        {error && <div className="card" style={{ color: "var(--warn)" }}>{error}</div>}

        <button type="submit" disabled={busy || opponentId === null} style={{ width: "100%" }}>
          {busy ? "Starting…" : "Start game"}
        </button>
      </form>
    </div>
  );
}
